import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Card, TextField, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, Avatar, Dialog, DialogTitle,
  DialogContent, DialogActions, Grid, MenuItem, CircularProgress, Alert,
  IconButton, InputAdornment
} from '@mui/material';
import { PersonAdd, Search, Close } from '@mui/icons-material';
import api from '../../api/axios';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';

const roles = ['admin', 'doctor', 'nurse', 'receptionist', 'pharmacist', 'accountant'];
const roleColors: Record<string, { bg: string; color: string }> = {
  admin:        { bg: '#dbeafe', color: '#1d4ed8' },
  doctor:       { bg: '#dcfce7', color: '#15803d' },
  nurse:        { bg: '#ede9fe', color: '#6d28d9' },
  receptionist: { bg: '#fce7f3', color: '#be185d' },
  pharmacist:   { bg: '#cffafe', color: '#0e7490' },
  accountant:   { bg: '#ffedd5', color: '#c2410c' },
  superadmin:   { bg: '#fef9c3', color: '#a16207' },
};

const defaultForm = { name: '', email: '', password: '', role: 'receptionist', phone: '', specialization: '', licenseNumber: '' };

const Staff = () => {
  const { user } = useAuth();
  const [staff, setStaff] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<any>(defaultForm);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      // Using auth/register endpoint's hospital context — staff are users under the same hospital
      const res = await api.get('/auth/staff', { params: { search } }).catch(() => ({ data: { data: [] } }));
      setStaff(res.data.data || []);
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => { load(); }, [load]);

  const handleSubmit = async () => {
    if (!form.name || !form.email || !form.password) { setError('Name, email and password are required'); return; }
    setError(''); setSubmitting(true);
    try {
      await api.post('/auth/register', { ...form, hospitalId: user?.hospital?._id });
      toast.success('Staff member added successfully');
      setOpen(false);
      setForm(defaultForm);
      load();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to add staff member');
    } finally { setSubmitting(false); }
  };

  const set = (k: string, v: string) => setForm((f: any) => ({ ...f, [k]: v }));

  // Show sample staff since /auth/staff might not exist yet
  const displayStaff = staff.length > 0 ? staff : [];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a' }}>Staff Management</Typography>
          <Typography variant="body2" sx={{ color: '#64748b', mt: 0.3 }}>Manage hospital staff and roles</Typography>
        </Box>
        <Button variant="contained" startIcon={<PersonAdd />} onClick={() => { setOpen(true); setError(''); setForm(defaultForm); }}
          sx={{ borderRadius: 2, bgcolor: '#1e40af', '&:hover': { bgcolor: '#1d4ed8' }, textTransform: 'none', fontWeight: 600, px: 2.5 }}>
          Add Staff
        </Button>
      </Box>

      <Card elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 3, p: 2, mb: 2 }}>
        <TextField fullWidth size="small" placeholder="Search staff by name or email..."
          value={search} onChange={e => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ color: '#94a3b8', fontSize: 20 }} /></InputAdornment> }}
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#f8fafc' } }} />
      </Card>

      <Card elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 3, overflow: 'hidden' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress sx={{ color: '#3b82f6' }} /></Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                  {['Staff Member', 'Role', 'Phone', 'Specialization', 'Status'].map(h => (
                    <TableCell key={h} sx={{ fontWeight: 700, color: '#374151', py: 1.5, fontSize: 13, borderBottom: '2px solid #e2e8f0' }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {displayStaff.map((s: any) => {
                  const rc = roleColors[s.role] || roleColors.admin;
                  return (
                    <TableRow key={s._id} hover sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Avatar sx={{ width: 36, height: 36, bgcolor: rc.bg, color: rc.color, fontSize: 14, fontWeight: 700 }}>
                            {s.name?.charAt(0).toUpperCase()}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#0f172a' }}>{s.name}</Typography>
                            <Typography variant="caption" sx={{ color: '#94a3b8' }}>{s.email}</Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip label={s.role} size="small" sx={{ bgcolor: rc.bg, color: rc.color, fontWeight: 600, fontSize: 11, textTransform: 'capitalize' }} />
                      </TableCell>
                      <TableCell><Typography variant="body2">{s.phone || '—'}</Typography></TableCell>
                      <TableCell><Typography variant="body2">{s.specialization || '—'}</Typography></TableCell>
                      <TableCell>
                        <Chip label={s.isActive ? 'Active' : 'Inactive'} size="small"
                          sx={{ bgcolor: s.isActive ? '#dcfce7' : '#f1f5f9', color: s.isActive ? '#15803d' : '#64748b', fontWeight: 600, fontSize: 11 }} />
                      </TableCell>
                    </TableRow>
                  );
                })}
                {displayStaff.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 8 }}>
                      <Typography sx={{ color: '#94a3b8' }}>No staff members found. Add your first staff member.</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      {/* Add Staff Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 700, color: '#0f172a' }}>
          Add Staff Member
          <IconButton size="small" onClick={() => setOpen(false)}><Close fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b', mb: 0.5, display: 'block' }}>Full Name *</Typography>
              <TextField fullWidth size="small" value={form.name} onChange={e => set('name', e.target.value)} placeholder="Enter full name" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b', mb: 0.5, display: 'block' }}>Email *</Typography>
              <TextField fullWidth size="small" type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="Email address" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b', mb: 0.5, display: 'block' }}>Password *</Typography>
              <TextField fullWidth size="small" type="password" value={form.password} onChange={e => set('password', e.target.value)} placeholder="Min 6 characters" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b', mb: 0.5, display: 'block' }}>Phone</Typography>
              <TextField fullWidth size="small" value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="Mobile number" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b', mb: 0.5, display: 'block' }}>Role *</Typography>
              <TextField select fullWidth size="small" value={form.role} onChange={e => set('role', e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
                {roles.map(r => <MenuItem key={r} value={r} sx={{ textTransform: 'capitalize' }}>{r.charAt(0).toUpperCase() + r.slice(1)}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b', mb: 0.5, display: 'block' }}>Specialization</Typography>
              <TextField fullWidth size="small" value={form.specialization} onChange={e => set('specialization', e.target.value)} placeholder="e.g. Cardiology" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b', mb: 0.5, display: 'block' }}>License Number</Typography>
              <TextField fullWidth size="small" value={form.licenseNumber} onChange={e => set('licenseNumber', e.target.value)} placeholder="Medical license number" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setOpen(false)} sx={{ textTransform: 'none', color: '#64748b' }}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={submitting}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, bgcolor: '#1e40af', '&:hover': { bgcolor: '#1d4ed8' }, px: 3 }}>
            {submitting ? <CircularProgress size={18} sx={{ color: 'white' }} /> : 'Add Staff'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Staff;
